var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useCallback, useEffect, useMemo, useState } from "react";
import * as pc from "playcanvas";
import { boxRenderHeight } from "../cardboard-builder/transform-helpers";
import { useAssetLoader } from "hooks/playcanvas/asset-loader";
import { useAppSelector, selectors } from "store";
import { objectPosition } from "3d/constants/common";
import { palletTopWorldY } from "3d/box-stack";
import { buildPalletSlats } from "3d/pallet-slats";
import { applyMaterialTransforms, getMaterialAssets, } from "3d/material-effects";
import { Material } from "3d/types";
export var PALLET_ENTITY_NAME = "Pallet";
// The 100 cm cube primitive; scaled per slat it becomes a deck board / stringer.
var CUBE_ASSET = "Primitive-Cube100x100x100cm";
// Recolor the cube's default material to a wooden brown.
var PALLET_MATERIAL_TRANSFORMS = [
    {
        sourceMaterial: Material.MA_TESTOBJECT,
        targetMaterial: Material.PLASTIC_GLOSSY_BROWN,
    },
];
/**
 * Renders the wooden pallet the cardboard boxes stand on. A single shared
 * footprint centred under `objectPosition`, sized to the scene's pallet
 * dimensions, and pushed down by one box height per stacked layer so the
 * whole stack rests on it (see `3d/box-stack`). Purely visual — the "box off
 * the pallet" check is a 2D selector (`selectBoxesOutsidePallet`), so the
 * pallet has no collision body. Rendered only in the cardboard application, and
 * only once the boxes are palletized — a single box is not standing on a pallet
 * (see canvas.tsx).
 */
export var PalletBuilder = function (_a) {
    var app = _a.app, modelAssets = _a.modelAssets, materialAssets = _a.materialAssets;
    var pallet = useAppSelector(selectors.selectPallet);
    var layersInUse = useAppSelector(selectors.selectLayersInUse);
    var loadAssets = useAssetLoader(app).loadAssets;
    var _b = __read(useState(), 2), rootEntity = _b[0], setRootEntity = _b[1];
    var topWorldY = palletTopWorldY(layersInUse.length, boxRenderHeight, objectPosition.y);
    var slats = useMemo(function () { return buildPalletSlats(pallet.length, pallet.width); }, [pallet.length, pallet.width]);
    // Create the pallet root once.
    useEffect(function () {
        var entity = app.root.findByName(PALLET_ENTITY_NAME);
        if (!entity) {
            entity = new pc.Entity(PALLET_ENTITY_NAME);
            app.root.addChild(entity);
        }
        setRootEntity(entity);
        return function () {
            entity === null || entity === void 0 ? void 0 : entity.destroy();
        };
    }, [app.root]);
    // Keep the pallet under the stack: adding or removing the second layer
    // moves it down or up by a box height.
    useEffect(function () {
        rootEntity === null || rootEntity === void 0 ? void 0 : rootEntity.setPosition(objectPosition.x, topWorldY, objectPosition.z);
    }, [rootEntity, topWorldY]);
    // (Re)build the slats whenever the footprint changes.
    var buildSlats = useCallback(function (root) {
        var cubeAsset = modelAssets[CUBE_ASSET];
        if (!cubeAsset)
            return;
        // Remove previous slats before regenerating.
        for (var i = root.children.length - 1; i >= 0; i--) {
            root.children[i].destroy();
        }
        var slatEntities = slats.map(function (slat) {
            var entity = new pc.Entity("Pallet-Slat");
            entity.addComponent("model", { type: "asset" });
            entity.setLocalScale.apply(entity, __spreadArray([], __read(slat.scale), false));
            entity.setLocalPosition.apply(entity, __spreadArray([], __read(slat.position), false));
            root.addChild(entity);
            return entity;
        });
        var materialTransforms = getMaterialAssets(materialAssets, PALLET_MATERIAL_TRANSFORMS);
        var assetsToLoad = [cubeAsset];
        if (materialTransforms) {
            assetsToLoad.push.apply(assetsToLoad, __spreadArray([], __read(Object.values(materialTransforms)), false));
        }
        loadAssets(assetsToLoad, function () {
            slatEntities.forEach(function (entity) {
                if (!entity.model)
                    return;
                // Re-assign the asset (see ApplicationObject) so the material
                // transform takes effect.
                entity.model.asset = 0;
                entity.model.asset = cubeAsset;
                applyMaterialTransforms(entity, materialTransforms);
            });
        });
    }, [slats, modelAssets, materialAssets, loadAssets]);
    useEffect(function () {
        if (!rootEntity)
            return;
        buildSlats(rootEntity);
    }, [rootEntity, buildSlats]);
    return null;
};
